/**
 * Editor.js inline tool: remove a link. POC for the narrative editor — see
 * _planning/narrative_editor_poc.md.
 *
 * Deliberately source-agnostic: it unwraps whatever <a> the caret sits in,
 * so it clears generic links (from the built-in `link` tool) and the custom
 * finding links (findingLinkTool.ts) with the same button. It targets the
 * tag, not any tool-specific marker, which is exactly why one button covers
 * both cases.
 */
import type { API, InlineTool } from '@editorjs/editorjs';
// Editor.js's own unlink glyph, so the button matches the built-in tools'
// icon size and weight exactly.
import { IconUnlink } from '@codexteam/icons';

export class RemoveLinkTool implements InlineTool {
    static get isInline(): boolean {
        return true;
    }

    static get title(): string {
        return 'Remove link';
    }

    private api: API;

    constructor({ api }: { api: API }) {
        this.api = api;
    }

    render(): HTMLElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.classList.add('ce-inline-tool');
        button.innerHTML = IconUnlink;
        return button;
    }

    surround(): void {
        const anchor = this.api.selection.findParentTag('A');
        if (anchor) this.unwrap(anchor);
    }

    /** Replace the anchor with its own children, then merge adjacent text. */
    private unwrap(anchor: HTMLElement): void {
        const parent = anchor.parentNode;
        if (!parent) return;
        while (anchor.firstChild) {
            parent.insertBefore(anchor.firstChild, anchor);
        }
        parent.removeChild(anchor);
        parent.normalize();
    }

    /** Highlight the button only when the caret is actually inside a link. */
    checkState(): boolean {
        return this.api.selection.findParentTag('A') !== null;
    }
}
